import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from 'contexts/authContext';
import {
  MessageContextActionType,
  useMessageContext,
} from 'contexts/messageContext';
import { useNavigate } from 'react-router-dom';

type MessageContent =
  | { name: 'hashchange'; payload: Location }
  | { name: 'generatedInvoice'; payload: { jobId: string } }
  | { name: 'sessionExpired'; payload: null };

type Props = {
  pathname?: string;
  isHidden: boolean;
};

export const IFramedComponent = forwardRef<HTMLIFrameElement, Props>(
  function IFrameBase({ pathname, isHidden }: Props, ref) {
    const [iframePathname, setIframePathname] = useState(pathname);
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const [iframeLoaded, setIframeLoaded] = useState(false);

    const { logoutUser } = useAuth();
    const { dispatch } = useMessageContext();
    const navigate = useNavigate();

    const handleMessageEvent = useCallback(
      (event: MessageEvent<MessageContent>) => {
        if (event.origin !== window.location.origin) return;

        switch (event.data.name) {
          case 'hashchange':
            if (event.data.payload.origin) {
              const { hash, search } = event.data.payload;

              const newPathname = hash.slice(1);
              !isHidden &&
                navigate({ pathname: newPathname, search }, { replace: true });
            }
            return;
          case 'generatedInvoice':
            dispatch({
              type: MessageContextActionType.SET_JOB_ID,
              jobId: event.data.payload.jobId,
            });
            return;
          case 'sessionExpired':
            logoutUser('session_expired');

          // no default
        }
      },
      [isHidden, navigate, dispatch, logoutUser],
    );

    useEffect(() => {
      window.addEventListener('message', handleMessageEvent);
      return () => window.removeEventListener('message', handleMessageEvent);
    }, [handleMessageEvent]);

    useEffect(() => {
      !pathname?.startsWith('/invoices') && setIframePathname(pathname);
    }, [pathname]);

    useEffect(() => {
      const el = document.getElementById('atlwdg-trigger');
      if (el) {
        if (pathname?.startsWith('/invoices') || pathname === '/') {
          el.style.display = 'block';
        } else {
          el.style.display = 'none';
        }
      }
    }, [pathname]);

    useEffect(() => {
      if (iframeLoaded && iframeRef.current?.contentWindow && iframePathname) {
        iframeRef.current.contentWindow.postMessage(
          { name: 'navigate', payload: iframePathname },
          window.location.origin,
        );
      }
    }, [iframePathname, iframeLoaded]);

    const handleIframeLoad = () => {
      setIframeLoaded(true);
      if (iframeRef.current?.contentWindow && iframePathname) {
        iframeRef.current.contentWindow.postMessage(
          { name: 'navigate', payload: iframePathname },
          window.location.origin,
        );
      }
    };

    return (
      <iframe
        ref={(node) => {
          (
            iframeRef as React.MutableRefObject<HTMLIFrameElement | null>
          ).current = node;
          if (typeof ref === 'function') {
            ref(node);
          } else if (ref) {
            ref.current = node;
          }
        }}
        onLoad={handleIframeLoad}
        title='original-app'
        src={`/backend/#${iframePathname}`}
        sandbox='allow-scripts allow-same-origin allow-popups allow-forms allow-downloads'
        width='100%'
        height='100%'
        loading='lazy'
      />
    );
  },
);
